'use client';

import {
  type ModalQueueGeneratedOpener,
  useModalQueue
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, { createContext, useMemo } from 'react';
import type { DeepPartial } from '~/types';
import { ModalId, ModalPriorities } from './constants';

type ContextType = {
  isAnyModalOpen: boolean;
  isGeolocationModalOpen: boolean;
  isPackageDetailsModalOpen: boolean;
  isPriceBreakdownModalOpen: boolean;
  setGeolocationModalOpen: ModalQueueGeneratedOpener;
  setPackageDetailsModalOpen: ModalQueueGeneratedOpener;
  setPriceBreakdownModalOpen: ModalQueueGeneratedOpener;
};

export const defaultContext: ContextType = {
  isAnyModalOpen: false,
  isGeolocationModalOpen: false,
  isPackageDetailsModalOpen: false,
  isPriceBreakdownModalOpen: false,
  setGeolocationModalOpen() {},
  setPackageDetailsModalOpen() {},
  setPriceBreakdownModalOpen() {}
};

const ModalContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren;

export function ModalContextProvider({ children }: OwnProps) {
  const [currentModalId, generateOpener] = useModalQueue(ModalPriorities);

  const { setGeolocationModalOpen,setPackageDetailsModalOpen, setPriceBreakdownModalOpen } = useMemo(
    () => ({
      setGeolocationModalOpen: generateOpener(ModalId.Geolocation),
      setPackageDetailsModalOpen: generateOpener(ModalId.PackageDetails),
      setPriceBreakdownModalOpen: generateOpener(ModalId.PriceBreakdown)
    }),
    [generateOpener]
  );

  const value = useMemo<ContextType>(
    (): ContextType => ({
      isAnyModalOpen: currentModalId > -1,
      isGeolocationModalOpen: currentModalId === ModalId.Geolocation,
      isPackageDetailsModalOpen: currentModalId === ModalId.PackageDetails,
      isPriceBreakdownModalOpen: currentModalId === ModalId.PriceBreakdown,
      setGeolocationModalOpen,
      setPackageDetailsModalOpen,
      setPriceBreakdownModalOpen
    }),
    [currentModalId, setGeolocationModalOpen, setPackageDetailsModalOpen, setPriceBreakdownModalOpen]
  );

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}

ModalContextProvider.displayName = 'ModalContextProvider';

export default ModalContext;

export function useModalContext() {
  return React.useContext(ModalContext);
}

export const generateModalContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function MockedModalContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<ContextType> }>) {
  return (
    <ModalContext.Provider value={generateModalContext(mockValue)}>
      {children}
    </ModalContext.Provider>
  );
}
